import React from 'react';
import styled from 'styled-components';
import {
  Users,
  Crown,
  Briefcase,
  ChevronRight,
  Award,
  Building2
} from 'lucide-react';
import medPhoto from '../assets/MED1.jpeg';
import sgPhoto from '../assets/SGG.jpeg';
import sgaPhoto from '../assets/SGA med.jpeg';
import dircabPhoto from '../assets/dircab.png';
import { useSiteContent } from '../hooks/useSiteContent';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 600px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const MediatorSection = styled.div`
  margin-top: -3rem;
  position: relative;
  z-index: 2;
  margin-bottom: 3rem;
`;

const MediatorCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
  display: grid;
  grid-template-columns: 350px 1fr;
  border: 1px solid var(--border-light);

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const MediatorPhotoContainer = styled.div`
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(254, 202, 0, 0.2) 0%, transparent 70%);
    border-radius: 50%;
  }

  @media (max-width: 900px) {
    padding: 2rem;
  }
`;

const MediatorPhoto = styled.div`
  width: 250px;
  height: 250px;
  border-radius: 50%;
  border: 6px solid var(--white);
  overflow: hidden;
  box-shadow: var(--shadow-xl);
  position: relative;
  z-index: 1;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center top;
  }

  @media (max-width: 900px) {
    width: 200px;
    height: 200px;
  }
`;

const MediatorInfo = styled.div`
  padding: 3rem;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 900px) {
    padding: 2rem;
    text-align: center;
  }
`;

const MediatorBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, var(--accent-gold), #ffd940);
  color: var(--primary-blue-dark);
  padding: 0.6rem 1.2rem;
  border-radius: var(--radius-full);
  font-size: 0.9rem;
  font-weight: 700;
  width: fit-content;
  margin-bottom: 1.5rem;

  @media (max-width: 900px) {
    margin: 0 auto 1.5rem;
  }
`;

const MediatorName = styled.h2`
  font-family: var(--font-secondary);
  font-size: 2.5rem;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const MediatorBio = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.8;
  margin-bottom: 2rem;
`;

const TeamSection = styled.div`
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.75rem;
  color: var(--primary-blue);
  margin-bottom: 2rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--accent-gold);
  }
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const TeamCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
  transition: var(--transition);
  text-align: center;

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-xl);
    border-color: var(--accent-gold);
  }
`;

const TeamPhoto = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);
  font-size: 3rem;
  border: 4px solid var(--border-light);
  transition: var(--transition);

  ${TeamCard}:hover & {
    border-color: var(--accent-gold);
  }

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    object-position: center top;
  }
`;

const TeamName = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
`;

const TeamRole = styled.p`
  font-size: 0.95rem;
  color: var(--primary-blue);
  font-weight: 600;
  margin-bottom: 1rem;
`;

const OtherSections = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SectionCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2rem;
  box-shadow: var(--shadow-md);
  border: 1px solid var(--border-light);
`;

const SectionCardTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-dark);
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-light);
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--accent-gold);
  }
`;

const ListItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  font-size: 0.95rem;
  color: var(--text-medium);
  border-bottom: 1px solid var(--border-light);

  &:last-child {
    border-bottom: none;
  }

  svg {
    color: var(--primary-blue);
    flex-shrink: 0;
  }
`;

const PHOTO_MAP = {
  mediateur: medPhoto,
  sg: sgPhoto,
  sga: sgaPhoto,
  dircab: dircabPhoto
};

const EquipeDirigeante = () => {
  const { content } = useSiteContent('equipe_dirigeante_page');
  const data = content || {};

  const secretariatGeneral = Array.isArray(data.secretariatGeneral) ? data.secretariatGeneral : [];
  const allDirections = Array.isArray(data.directions) ? data.directions : [];
  const conseillersTechniques = Array.isArray(data.conseillersTechniques) ? data.conseillersTechniques : [];
  const conseillersMissions = Array.isArray(data.conseillersMissions) ? data.conseillersMissions : [];

  const cabinetLabel = String(data.cabinetDirectionLabel || 'Direction de Cabinet').toLowerCase();
  const cabinetDirectionFromList = allDirections.find((item) => {
    if (typeof item !== 'string') return false;
    const normalized = item.trim().toLowerCase();
    return normalized.includes('cabinet') || normalized === cabinetLabel;
  });

  const directions = allDirections.filter((item) => {
    if (typeof item !== 'string') return false;
    const normalized = item.trim().toLowerCase();
    return !normalized.includes('cabinet') && normalized !== cabinetLabel;
  });

  const cabinetDirector = data.cabinetDirector || (cabinetDirectionFromList ? {
    name: data.cabinetDirectorName || 'MAHAMADOU MAHAMAT SOULEYMANE',
    role: data.cabinetDirectorRole || 'Directeur de Cabinet'
  } : null);

  const mediatorPhoto = data.mediatorPhoto || PHOTO_MAP[data.mediatorPhotoKey] || medPhoto;

  const resolveMemberPhoto = (member) => {
    if (member?.photo) return member.photo;
    if (member?.photoKey && PHOTO_MAP[member.photoKey]) return PHOTO_MAP[member.photoKey];
    return null;
  };

  const cabinetPhoto = resolveMemberPhoto(cabinetDirector) || dircabPhoto;

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>{data.heroTitle || 'Équipe dirigeante'}</HeroTitle>
          <HeroSubtitle>
            {data.heroSubtitle || "Découvrez les membres de l'équipe qui font fonctionner la Médiature de la République"}
          </HeroSubtitle>
        </HeroContent>
      </Hero>

      <Container>
        <MediatorSection>
          <MediatorCard>
            <MediatorPhotoContainer>
              <MediatorPhoto>
                <img src={mediatorPhoto} alt={data.mediatorName || 'Médiateur'} />
              </MediatorPhoto>
            </MediatorPhotoContainer>
            <MediatorInfo>
              <MediatorBadge>
                <Crown size={20} />
                {data.mediatorBadge || 'Médiateur de la République'}
              </MediatorBadge>
              <MediatorName>{data.mediatorName || ''}</MediatorName>
              <MediatorBio>{data.mediatorBio || ''}</MediatorBio>
            </MediatorInfo>
          </MediatorCard>
        </MediatorSection>

        <TeamSection>
          <SectionTitle>
            <Users size={28} />
            {data.secretariatTitle || 'Secrétariat général'}
          </SectionTitle>
          <TeamGrid>
            {secretariatGeneral.map((directeur, index) => {
              const memberPhoto = resolveMemberPhoto(directeur);
              return (
                <TeamCard key={`${directeur.name || 'member'}-${index}`}>
                  <TeamPhoto>
                    {memberPhoto ? (
                      <img src={memberPhoto} alt={directeur.name || 'Membre'} />
                    ) : (
                      <Briefcase size={48} />
                    )}
                  </TeamPhoto>
                  <TeamName>{directeur.name || ''}</TeamName>
                  <TeamRole>{directeur.role || ''}</TeamRole>
                </TeamCard>
              );
            })}
          </TeamGrid>
        </TeamSection>

        {cabinetDirector && (
          <TeamSection>
            <SectionTitle>
              <Briefcase size={28} />
              {data.cabinetTitle || 'Direction de Cabinet'}
            </SectionTitle>
            <TeamGrid>
              <TeamCard>
                <TeamPhoto>
                  {cabinetPhoto ? (
                    <img src={cabinetPhoto} alt={cabinetDirector.name || 'Directeur de Cabinet'} />
                  ) : (
                    <Briefcase size={48} />
                  )}
                </TeamPhoto>
                <TeamName>{cabinetDirector.name || 'MAHAMADOU MAHAMAT SOULEYMANE'}</TeamName>
                <TeamRole>{cabinetDirector.role || 'Directeur de Cabinet'}</TeamRole>
              </TeamCard>
            </TeamGrid>
          </TeamSection>
        )}

        <OtherSections>
          <SectionCard>
            <SectionCardTitle>
              <Building2 size={22} />
              {data.directionsTitle || 'Directions'}
            </SectionCardTitle>
            {directions.map((direction, index) => (
              <ListItem key={`dir-${index}`}>
                <ChevronRight size={18} />
                {direction}
              </ListItem>
            ))}
          </SectionCard>

          <SectionCard>
            <SectionCardTitle>
              <Award size={22} />
              {data.conseillersTechniquesTitle || 'Conseillers techniques'} ({conseillersTechniques.length})
            </SectionCardTitle>
            {conseillersTechniques.map((conseiller, index) => (
              <ListItem key={`ct-${index}`}>
                <ChevronRight size={18} />
                {conseiller}
              </ListItem>
            ))}
          </SectionCard>

          <SectionCard>
            <SectionCardTitle>
              <Briefcase size={22} />
              {data.conseillersMissionsTitle || 'Conseillers chargés de mission'} ({conseillersMissions.length})
            </SectionCardTitle>
            {conseillersMissions.map((conseiller, index) => (
              <ListItem key={`cm-${index}`}>
                <ChevronRight size={18} />
                {conseiller}
              </ListItem>
            ))}
          </SectionCard>
        </OtherSections>
      </Container>
    </PageContainer>
  );
};

export default EquipeDirigeante;
